import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";
import { Download, Loader2, Search } from "lucide-react";

import { AdminShell } from "@/platform/layout/admin-shell";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Input } from "@/platform/ui/input";
import { fetchAdminAudit, type AdminAuditEntry } from "@/platform/admin/api";
import { cn } from "@/platform/lib/cn";
import { downloadCsv } from "@/platform/lib/csv";

const ACTION_LABELS: Record<string, string> = {
  set_admin: "Tornar/remover admin",
  ban: "Banir",
  unban: "Desbanir",
  send_password_reset: "Reset de senha",
  delete_user: "Deletar conta",
  cancel_subscription: "Cancelar assinatura",
  replace_page_image: "Substituir imagem de página",
};

const ACTION_TONES: Record<string, string> = {
  set_admin: "border-[color:var(--color-info)]/40 text-[color:var(--color-info)]",
  ban: "border-[color:var(--color-warning)]/40 text-[color:var(--color-warning)]",
  unban: "border-[color:var(--color-success)]/40 text-[color:var(--color-success)]",
  send_password_reset: "border-[color:var(--color-info)]/40 text-[color:var(--color-info)]",
  delete_user: "border-[color:var(--color-danger)]/40 text-[color:var(--color-danger)]",
  cancel_subscription: "border-[color:var(--color-danger)]/40 text-[color:var(--color-danger)]",
  replace_page_image: "border-[color:var(--color-warning)]/40 text-[color:var(--color-warning)]",
};

export function AdminAuditPage() {
  const [actorEmail, setActorEmail] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [exporting, setExporting] = useState(false);

  const query = useQuery({
    queryKey: ["admin", "audit", { actorEmail, targetEmail, action, page }],
    queryFn: () =>
      fetchAdminAudit({
        actorEmail: actorEmail.trim() || undefined,
        targetEmail: targetEmail.trim() || undefined,
        action: action || undefined,
        page,
        pageSize,
      }),
    staleTime: 15_000,
  });

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / pageSize)) : 1;

  async function handleExport() {
    setExporting(true);
    try {
      const all = await fetchAdminAudit({
        actorEmail: actorEmail.trim() || undefined,
        targetEmail: targetEmail.trim() || undefined,
        action: action || undefined,
        page: 1,
        pageSize: 1000,
      });
      if (all.entries.length === 0) {
        toast.info("Nada para exportar com esses filtros.");
        return;
      }
      downloadCsv(
        `auditoria_admin_${new Date().toISOString().slice(0, 10)}.csv`,
        all.entries,
        [
          { key: (e) => new Date(e.created_at).toLocaleString("pt-BR"), header: "Quando" },
          { key: "actor_email", header: "Admin" },
          {
            key: (e) => ACTION_LABELS[e.action] ?? e.action,
            header: "Ação",
          },
          { key: "target_type", header: "Tipo alvo" },
          { key: (e) => e.target_label ?? "", header: "Alvo (email)" },
          { key: "target_id", header: "target_id" },
          { key: (e) => JSON.stringify(e.payload), header: "Payload" },
        ],
      );
      toast.success(`Exportadas ${all.entries.length} entradas.`);
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao exportar."));
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminShell
      title="Auditoria"
      description="Registro imutável de quem fez o quê na área administrativa."
      actions={
        <Button variant="outline" size="sm" onClick={handleExport} loading={exporting}>
          <Download className="size-3.5" />
          Exportar CSV
        </Button>
      }
    >
      {/* Filtros */}
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Admin
          </label>
          <div className="mt-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[color:var(--color-muted-foreground)]" aria-hidden />
            <Input
              value={actorEmail}
              onChange={(e) => {
                setActorEmail(e.target.value);
                setPage(1);
              }}
              placeholder="email do admin"
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Alvo
          </label>
          <div className="mt-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[color:var(--color-muted-foreground)]" aria-hidden />
            <Input
              value={targetEmail}
              onChange={(e) => {
                setTargetEmail(e.target.value);
                setPage(1);
              }}
              placeholder="email da usuária afetada"
              className="pl-9"
            />
          </div>
        </div>
        <div>
          <label className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
            Ação
          </label>
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="mt-1 w-full sm:w-auto rounded-lg border border-[--color-border] bg-[color:var(--color-background)] px-3.5 py-2.5 text-[length:var(--text-body-sm)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]"
          >
            <option value="">Todas</option>
            {Object.entries(ACTION_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] overflow-hidden">
        {query.isLoading ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
          </div>
        ) : query.error ? (
          <p className="p-10 text-[color:var(--color-danger)] text-[length:var(--text-body-sm)]">
            Falha ao carregar auditoria.
          </p>
        ) : !query.data || query.data.entries.length === 0 ? (
          <p className="p-10 text-center text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
            Nenhuma ação administrativa registrada com esses filtros.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[length:var(--text-body-sm)]">
                <thead className="bg-[color:var(--color-muted)]/50">
                  <tr>
                    <Th>Quando</Th>
                    <Th>Admin</Th>
                    <Th>Ação</Th>
                    <Th>Alvo</Th>
                    <Th>Detalhe</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.entries.map((e) => (
                    <AuditRow key={e.id} entry={e} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-[--color-border] px-4 py-3 text-[length:var(--text-body-sm)]">
              <span className="text-[color:var(--color-muted-foreground)]">
                {query.data.total.toLocaleString("pt-BR")} entradas · página {page} de {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

function AuditRow({ entry }: { entry: AdminAuditEntry }) {
  const targetLink =
    entry.target_type === "user"
      ? `/admin/usuarios/${entry.target_id}`
      : `/admin/assinaturas`; // não há detalhe individual de sub; link para a lista

  return (
    <tr className="border-t border-[--color-border]">
      <td className="px-4 py-3 text-[color:var(--color-muted-foreground)] whitespace-nowrap">
        {new Date(entry.created_at).toLocaleString("pt-BR")}
      </td>
      <td className="px-4 py-3">{entry.actor_email}</td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={ACTION_TONES[entry.action] ?? ""}>
          {ACTION_LABELS[entry.action] ?? entry.action}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <Link to={targetLink} className="hover:underline">
          {entry.target_label ?? entry.target_id.slice(0, 8)}
        </Link>
        <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          {entry.target_type}
        </p>
      </td>
      <td className="px-4 py-3 text-[color:var(--color-muted-foreground)]">
        <code className="text-[length:var(--text-caption)]">
          {Object.keys(entry.payload).length > 0 ? JSON.stringify(entry.payload) : "—"}
        </code>
      </td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      className={cn(
        "px-4 py-3 text-left text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)] font-medium",
      )}
    >
      {children}
    </th>
  );
}
